import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import {
  LanguageCode,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  translate,
  detectBrowserLanguage,
} from '@/i18n/translations';

const STORAGE_KEY = 'kc:language';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => Promise<void>;
  t: (key: string, fallback?: string) => string;
  supported: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Resolve the initial language synchronously so the very first render is
 * already in the right language (no flicker):
 *   1. localStorage value (returning visitor or guest)
 *   2. browser/device language if supported
 *   3. DEFAULT_LANGUAGE
 *
 * The logged-in user's stored preference (profiles.language) is hydrated
 * asynchronously after auth resolves and overrides the local value if it
 * differs.
 */
function resolveInitialLanguage(): LanguageCode {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as LanguageCode | null;
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch {
    /* ignore */
  }
  return detectBrowserLanguage();
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => resolveInitialLanguage());

  // Hydrate from the logged-in user's profile, if they have one stored
  // server-side. We do this lazily so we don't have to wire a hard
  // dependency on AuthContext (LanguageProvider sits OUTSIDE AuthProvider
  // so guests on the public /join page also get translations).
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) return;
        const { data, error } = await supabase
          .from('profiles')
          .select('language')
          .eq('id', userId)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          console.warn('[Language] failed to load preference', error.message);
          return;
        }
        const remote = data?.language as LanguageCode | null | undefined;
        if (remote && SUPPORTED_LANGUAGES.some((l) => l.code === remote)) {
          setLanguageState(remote);
          try {
            window.localStorage.setItem(STORAGE_KEY, remote);
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.warn('[Language] hydrate exception', err);
      }
    };
    hydrate();

    // Re-hydrate when the user signs in/out so a freshly-logged-in user
    // immediately gets their saved language preference.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        hydrate();
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Reflect language on the <html lang="…"> attribute for accessibility +
  // browser hinting (spellcheck, screen readers, hyphenation).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  const setLanguage = useCallback(async (lang: LanguageCode) => {
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === lang)) return;
    setLanguageState(lang);
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    // Persist to profile if signed in (best-effort, non-blocking for UI)
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (userId) {
        await supabase
          .from('profiles')
          .update({ language: lang, updated_at: new Date().toISOString() })
          .eq('id', userId);
      }
    } catch (err) {
      console.warn('[Language] failed to persist preference', err);
    }
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => translate(key, language, fallback),
    [language]
  );

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    supported: SUPPORTED_LANGUAGES,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Defensive fallback so a component rendered outside the provider
    // (e.g. during a hot-reload edge case) still works with English.
    return {
      language: DEFAULT_LANGUAGE,
      setLanguage: async () => {},
      t: (key: string, fallback?: string) => fallback ?? key,
      supported: SUPPORTED_LANGUAGES,
    } as LanguageContextType;
  }
  return ctx;
};

/** Convenience hook for components that only need the translator. */
export const useT = () => useLanguage().t;
